import React, { useCallback, useEffect, useState } from "react";
import { type ColDef, themeQuartz } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";

import { IEventSummary, IWeightPullWeighIn } from "@eagerdog/interfaces";

import { apiService } from "src/services/api.service";
import { Modal, useModal } from "src/components/Modal/Modal";
import { toast } from "src/components/Toast/ToastManager";

import EnterWeightModal from "./EnterWeightModal";
import { WeightCellRenderer } from "./WeightCellRenderer";
import { EnterWeightCellRenderer } from "./EnterWeightCellRenderer";

import styles from "./WeighInsModal.module.scss";

interface IProps {
  event: IEventSummary;
}

// Event-level UKC Weight Pull "Weigh Ins" (see docs/ukc-weight-pull-weigh-in-redesign.md).
// Lists every unique dog entered in at least one Weight Pull trial at this
// event, and lets staff record each dog's weight once — it's automatically
// applied to every trial that dog is entered in.
const WeighInsModal: React.FC<IProps> = (props) => {
  const [rowData, setRowData] = useState<IWeightPullWeighIn[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [selectedDog, setSelectedDog] = useState<IWeightPullWeighIn | undefined>(undefined);

  const { isShown: isEnterWeightShown, toggle: toggleEnterWeight } = useModal();

  const fetchWeighIns = useCallback(() => {
    setLoading(true);
    apiService
      .getEventWeighIns(props.event._id)
      .then((data) => { setRowData(data); })
      .catch((error: any) => {
        toast.show({
          title: "Weigh Ins",
          content: "Unable to load Weight Pull entries for this event",
          duration: 10000,
          errorDetails: error,
          type: "fail",
        });
      })
      .finally(() => setLoading(false));
  }, [props.event._id]);

  useEffect(() => {
    fetchWeighIns();
  }, [fetchWeighIns]);

  const theme = themeQuartz.withParams({
    fontFamily: "Roboto",
    headerFontFamily: "Roboto",
    cellFontFamily: "Roboto",
  });

  const closeEnterWeight = () => {
    toggleEnterWeight();
    setSelectedDog(undefined);
  };

  const colDefs: ColDef<IWeightPullWeighIn>[] = [
    { headerName: "Armband", field: "arm_number", width: 140, minWidth: 140, maxWidth: 150, suppressSizeToFit: true, cellDataType: "text", filter: true },
    { headerName: "Registration #", field: "registration_number", width: 170, minWidth: 170, maxWidth: 180, suppressSizeToFit: true, cellDataType: "text", filter: true },
    { headerName: "Call Name", field: "call_name", minWidth: 130, flex: 1, filter: true },
    { headerName: "Weight", field: "dog_weight", minWidth: 100, flex: 1, cellRenderer: WeightCellRenderer },
    {
      headerName: "",
      field: "dog_id",
      width: 140,
      minWidth: 140,
      maxWidth: 140,
      suppressSizeToFit: true,
      resizable: false,
      sortable: false,
      filter: false,
      cellRenderer: EnterWeightCellRenderer,
      cellRendererParams: {
        onEnterWeight: (row: IWeightPullWeighIn) => {
          setSelectedDog(row);
          toggleEnterWeight();
        },
      },
    },
  ];

  return (
    <div className={styles.WeighInsModal}>
      <h2>Weigh Ins</h2>
      <p className={styles.subtitle}>
        Record each dog's weight once — it's automatically applied to every Weight Pull trial
        they're entered in at this event.
      </p>
      <div className={styles.tableWrap}>
        <AgGridReact
          suppressRowTransform
          rowData={rowData}
          columnDefs={colDefs}
          defaultColDef={{ resizable: true }}
          theme={theme}
        />
        {loading && <div className={styles.loadingOverlay}>Loading…</div>}
        {!loading && rowData.length === 0 && (
          <div className={styles.emptyState}>No dogs are entered in a Weight Pull show/trial yet.</div>
        )}
      </div>

      <Modal
        className="enterWeightModal"
        isShown={isEnterWeightShown}
        hide={closeEnterWeight}
        modalContent={
          selectedDog ? (
            <EnterWeightModal
              dog={selectedDog}
              eventId={props.event._id}
              onSave={(updated) => {
                setRowData(updated);
                closeEnterWeight();
              }}
              onCancel={closeEnterWeight}
            />
          ) : (
            <></>
          )
        }
      />
    </div>
  );
};

export default WeighInsModal;
